import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  Avatar,
  Paper
} from '@mui/material';
import EmergencyChronometer from './EmergencyChronometer';
import {
  Warning as EmergencyIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

interface EmergencyAlertProps {
  open: boolean;
  onClose: () => void;
  patientName: string;
  triggeredBy?: string;
  isProxyTrigger?: boolean;
}

const EmergencyAlert: React.FC<EmergencyAlertProps> = ({
  open,
  onClose,
  patientName,
  triggeredBy = '',
  isProxyTrigger = false
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [notificationsSent, setNotificationsSent] = useState(false);

  const steps = [
    'Alerta Activada',
    'Notificando Contactos',
    'Esperando Respuesta',
    'Ayuda en Camino'
  ];

  const familyContacts = [
    { name: 'María González', role: 'Cuidadora', phone: '+56912345678' },
    { name: 'Carlos González', role: 'Contacto Emergencia', phone: '+56987654321' },
    { name: 'Ana López', role: 'Observadora', phone: '+56911223344' }
  ];

  useEffect(() => {
    if (open && activeStep === 0) {
      // Simulate progression through steps
      const timer = setTimeout(() => {
        setActiveStep(1);
        simulateNotifications();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open, activeStep]);

  const simulateNotifications = () => {
    setTimeout(() => {
      setNotificationsSent(true);
      setActiveStep(2);
    }, 2000);

    setTimeout(() => {
      setActiveStep(3);
    }, 5000);
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'error.main', mx: 'auto', mb: 2 }}>
              <EmergencyIcon sx={{ fontSize: 48 }} />
            </Avatar>
            <Typography variant="h5" gutterBottom>
              ¡Emergencia Activada!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isProxyTrigger
                ? `${triggeredBy} ha activado emergencia para ${patientName}`
                : `${patientName} ha activado el botón de emergencia`}
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Enviando notificaciones...
            </Typography>
            <List>
              {familyContacts.map((contact, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {contact.name.charAt(0)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={contact.name}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <WhatsAppIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <SmsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="caption">{contact.phone}</Typography>
                      </Box>
                    }
                  />
                  <Chip label={contact.role} size="small" />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Notificaciones enviadas exitosamente
            </Alert>
            
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.light' }}>
              <Typography variant="subtitle2" gutterBottom>
                <CheckIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                María González respondió hace 30 segundos
              </Typography>
              <Typography variant="body2">
                "Voy en camino, llegaré en 10 minutos"
              </Typography>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <TimerIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
              Esperando respuesta de otros contactos...
            </Typography>

            <Alert severity="info">
              Si no hay respuesta en 5 minutos, se realizarán llamadas automáticas
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Ayuda en Camino
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="María González"
                  secondary="Llegará en aproximadamente 8 minutos"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LocationIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Ubicación compartida"
                  secondary={`Ubicación de ${patientName} enviada a todos los contactos`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Línea abierta"
                  secondary="Llamada activa con María González"
                />
              </ListItem>
            </List>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={activeStep === 3 ? onClose : undefined}
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={activeStep < 3}
    >
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmergencyIcon />
          Protocolo de Emergencia Activado
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        <EmergencyChronometer 
          startTime={new Date(Date.now() - 8 * 60 * 1000).toISOString()}
          variant="detailed"
          size="medium"
        />

        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent()}
      </DialogContent>

      <DialogActions>
        {activeStep === 3 && (
          <>
            <Button onClick={onClose}>Cerrar</Button>
            <Button 
              variant="contained" 
              onClick={() => window.open('tel:131')}
              color="error"
            >
              Llamar 131
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmergencyAlert;